import { useState, useEffect } from 'react';
import { Globe, Heart, FileText, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import PageSEO from '../components/PageSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const engagements = [
  {
    icon: FileText,
    title: 'Transparence Totale',
    description: 'Chaque projet financé ou soutenu par la diaspora fera l\'objet d\'un rapport public détaillé.',
  },
  {
    icon: Globe,
    title: 'Guichet Unique Numérique',
    description: 'Priorité à vos demandes administratives via un espace sécurisé.',
  },
  {
    icon: Heart,
    title: 'Facilitation de l\'Investissement',
    description: 'Accompagnement personnalisé pour vos projets à Gandiaye.',
  },
];

const projectCategories = [
  { value: 'education', label: 'Éducation' },
  { value: 'sante', label: 'Santé' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'environnement', label: 'Environnement' },
  { value: 'autre', label: 'Autre' },
];

export const DiasporaPage = () => {
  const [page, setPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    proposer_name: '',
    proposer_email: '',
    proposer_country: '',
    budget_estimate: '',
    category: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pages/diaspora`).then(r => setPage(r.data)).catch(() => {});
  }, []);

  const getSection = (key) => page?.sections?.find(s => s.key === key);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/diaspora/projects`, formData);
      setIsSubmitted(true);
      toast.success('Votre projet a été soumis avec succès !');
    } catch (error) {
      console.error('Error submitting project:', error);
      toast.error('Erreur lors de la soumission. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <main className="pt-14 md:pt-16" data-testid="diaspora-page">
      <PageSEO
        title="Espace Diaspora"
        description="Espace dédié à la diaspora de Gandiaye. Contribuez au développement de votre commune d'origine."
        path="/diaspora"
      />
      {/* Hero */}
      <section className="bg-emerald-600 py-16 md:py-24">
        <div className="container-main">
          <div className="flex items-center gap-3 text-gold-400 mb-4">
            <Globe size={24} />
            <span className="font-semibold uppercase tracking-wider text-sm">Espace Diaspora</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
            {getSection('intro')?.title || 'Bâtir Gandiaye Ensemble'}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            {getSection('intro')?.content || "Fils et filles de Gandiaye établis à travers le monde, votre expertise et votre attachement à nos racines sont indispensables pour la transformation de notre commune."}
          </p>
        </div>
      </section>

      {/* Charter */}
      <section className="py-16 bg-white">
        <div className="container-main">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-heading font-bold text-neutral-800 mb-6 text-center">
              Charte de la Diaspora
            </h2>
            <p className="text-neutral-600 text-center mb-12 max-w-2xl mx-auto">
              {getSection('objectifs')?.content || "Où que vous soyez, Gandiaye reste votre foyer. Cette charte scelle un pacte de confiance entre la Municipalité et sa Diaspora."}
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {engagements.map((item, index) => (
                <Card key={index} className="card-teranga text-center">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <item.icon className="text-emerald-600" size={28} />
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-neutral-600 text-sm">{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-gold-400/10 border-none">
              <CardContent className="p-8">
                <h3 className="font-heading font-semibold text-xl mb-4 text-center">
                  Votre Rôle dans le Développement
                </h3>
                <div className="grid md:grid-cols-3 gap-6 text-sm">
                  <div className="text-center">
                    <div className="w-10 h-10 bg-gold-400 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold">1</div>
                    <h4 className="font-semibold mb-2">Parrainage de Projets</h4>
                    <p className="text-neutral-600">Financez directement des micro-projets locaux.</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-gold-400 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold">2</div>
                    <h4 className="font-semibold mb-2">Transfert de Compétences</h4>
                    <p className="text-neutral-600">Participez à des forums virtuels ou missions.</p>
                  </div>
                  <div className="text-center">
                    <div className="w-10 h-10 bg-gold-400 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold">3</div>
                    <h4 className="font-semibold mb-2">Ambassadeurs</h4>
                    <p className="text-neutral-600">Promouvez Gandiaye dans vos pays d'accueil.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Project Submission Form */}
      <section className="py-16 bg-neutral-50" data-testid="diaspora-project-form">
        <div className="container-main">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-center">
                  <Send className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
                  Proposer un Projet pour Gandiaye
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                      Projet soumis avec succès !
                    </h3>
                    <p className="text-neutral-600 mb-6">
                      Merci pour votre contribution. Notre équipe examinera votre proposition 
                      et vous contactera prochainement.
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setIsSubmitted(false);
                        setFormData({
                          title: '',
                          description: '',
                          proposer_name: '',
                          proposer_email: '',
                          proposer_country: '',
                          budget_estimate: '',
                          category: '',
                        });
                      }}
                    >
                      Soumettre un autre projet
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Votre nom complet *
                        </label>
                        <Input
                          name="proposer_name"
                          value={formData.proposer_name}
                          onChange={handleChange}
                          required
                          placeholder="Prénom Nom"
                          data-testid="input-proposer-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Email *
                        </label>
                        <Input
                          type="email"
                          name="proposer_email"
                          value={formData.proposer_email}
                          onChange={handleChange}
                          required
                          placeholder="votre@email.com"
                          data-testid="input-proposer-email"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Pays de résidence *
                      </label>
                      <Input
                        name="proposer_country"
                        value={formData.proposer_country}
                        onChange={handleChange}
                        required
                        placeholder="Ex: France, États-Unis, Italie..."
                        data-testid="input-proposer-country"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Titre du projet *
                      </label>
                      <Input
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        required
                        placeholder="Ex: Rénovation de l'école primaire"
                        data-testid="input-project-title"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Catégorie *
                      </label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => setFormData({ ...formData, category: value })}
                      >
                        <SelectTrigger data-testid="select-category">
                          <SelectValue placeholder="Sélectionner une catégorie" />
                        </SelectTrigger>
                        <SelectContent>
                          {projectCategories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Description du projet *
                      </label>
                      <Textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Décrivez votre projet, ses objectifs et son impact potentiel..."
                        data-testid="input-project-description"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Budget estimé (optionnel)
                      </label>
                      <Input
                        name="budget_estimate"
                        value={formData.budget_estimate}
                        onChange={handleChange}
                        placeholder="Ex: 500 000 FCFA"
                        data-testid="input-budget"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6"
                      disabled={isSubmitting || !formData.category}
                      data-testid="submit-project-btn"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Envoi en cours...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send size={18} />
                          Soumettre mon projet
                        </span>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default DiasporaPage;
